// Widget "buy a gift card" panel.
//
// Sits alongside the main service tabs. Flow:
//   1. Purchaser picks amount (preset chips or custom) + fills in their
//      details + optional recipient details + optional personal message.
//   2. Widget calls create_gift_card_payment_intent → gets a Paystack
//      reference.
//   3. Redirects to Paystack hosted checkout.
//   4. On success the webhook issues the card + triggers the delivery
//      notification (both handled server-side in migration 088 + 087).
//
// No cart integration on purpose — a gift card is an independent
// transaction; mixing it into a cart with service bookings complicates
// refunds. Dedicated single-item checkout is the simpler UX.

import type React from "react";
import { useState, useTransition } from "react";
import type { WidgetTenant } from "../types";
import {
  createGiftCardPaymentIntent,
  initializePaystackCheckout,
} from "../state/booking";
import { describeInitError } from "../state/paystack-init-error";

const PRESETS = [500, 1000, 2500, 5000];

export function GiftCardPurchasePanel({ tenant }: { tenant: WidgetTenant }) {
  const [pending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);

  const [amountRands, setAmountRands] = useState<string>("1000");
  const [purchaserName, setPurchaserName] = useState("");
  const [purchaserEmail, setPurchaserEmail] = useState("");
  const [purchaserPhone, setPurchaserPhone] = useState("");
  const [isForSomeoneElse, setIsForSomeoneElse] = useState(false);
  const [recipientName, setRecipientName] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [recipientPhone, setRecipientPhone] = useState("");
  const [giftMessage, setGiftMessage] = useState("");

  function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);

    const amountCents = Math.round(parseFloat(amountRands || "0") * 100);
    if (!Number.isFinite(amountCents) || amountCents < 5000) {
      return setError("Minimum gift card is R50.");
    }
    if (!purchaserName.trim()) return setError("Your name is required.");
    if (!purchaserEmail.trim() || !purchaserEmail.includes("@")) {
      return setError("Valid email is required for the receipt.");
    }
    if (isForSomeoneElse && !recipientEmail.trim() && !recipientPhone.trim()) {
      return setError("Add a recipient email or phone so we can deliver the card.");
    }

    startTransition(async () => {
      const intent = await createGiftCardPaymentIntent({
        tenantId: tenant.id,
        amountCents,
        purchaser: {
          name: purchaserName.trim(),
          email: purchaserEmail.trim(),
          phone: purchaserPhone.trim() || undefined,
        },
        recipient: isForSomeoneElse
          ? {
              name: recipientName.trim() || undefined,
              email: recipientEmail.trim() || undefined,
              phone: recipientPhone.trim() || undefined,
              message: giftMessage.trim() || undefined,
            }
          : {
              name: purchaserName.trim(),
              email: purchaserEmail.trim(),
              phone: purchaserPhone.trim() || undefined,
            },
      });
      if (!intent.ok) {
        setError(intent.reason === "invalid_amount" ? "That amount isn't allowed." : "Couldn't start checkout.");
        return;
      }
      const init = await initializePaystackCheckout({
        reference: intent.reference,
        customerEmail: purchaserEmail.trim(),
        callbackUrl: window.location.href,
      });
      if (!init.ok) {
        // The checkout's words for the same refusals, never the bare code.
        setError(describeInitError(init.error, init.message));
        return;
      }
      window.location.href = init.authorization_url;
    });
  }

  const currency = tenant.currency ?? "ZAR";

  // Live preview values for the gold-gradient card on the left of the
  // gift-card grid. Falls back to "for someone special" when there's no
  // recipient name yet so the card always reads as a finished artefact.
  const previewAmount = Math.max(
    0,
    Math.floor(parseFloat(amountRands || "0")),
  );
  const previewRecipient = isForSomeoneElse
    ? recipientName.trim() || "For someone special"
    : purchaserName.trim() || "For someone special";

  return (
    <section className="vt-panel vt-giftcard-panel">
      <div className="vt-service-card">
      <div className="vt-hero">
        <div className="vt-hero-gradient vt-hero-giftcard" />
        <div className="vt-hero-overlay">
          <div className="vt-hero-overlay-text">
            <h2>Give the gift of a visit</h2>
            <p className="vt-hero-tag">
              A {tenant.name} gift card — sent by email, redeemable at booking.
            </p>
          </div>
        </div>
      </div>

      <div className="vt-service-body">
      {/* Two-column grid: card preview on the left, form on the right.
          Collapses to stacked single column under 720px (see styles). */}
      <div className="vt-giftcard-grid">
      <div className="vt-gift-preview" aria-hidden="true">
        <div className="vt-gift-preview-brand">
          {tenant.name}
          <span className="vt-gift-preview-brand-sub">Gift card</span>
        </div>
        <div className="vt-gift-preview-amount">
          <span className="vt-gift-preview-amount-ccy">{currency}</span>
          <span className="vt-gift-preview-amount-value">
            {previewAmount.toLocaleString()}
          </span>
        </div>
        <div className="vt-gift-preview-foot">{previewRecipient}</div>
      </div>

      <form className="vt-checkout" onSubmit={submit}>
        <div className="vt-giftcard-amounts">
          <div className="vt-section-label-row">
            <h4 className="vt-section-label-title">Choose amount</h4>
            <span className="vt-section-label-hint">{currency}</span>
          </div>
          <div className="vt-giftcard-presets">
            {PRESETS.map((amt) => (
              <button
                key={amt}
                type="button"
                className={`vt-amount-pill ${amountRands === String(amt) ? "vt-amount-pill-active" : ""}`}
                onClick={() => setAmountRands(String(amt))}
              >
                {currency} {amt.toLocaleString()}
              </button>
            ))}
          </div>
          <div className="vt-field">
            <label>Custom amount</label>
            <input
              type="number"
              min="50"
              step="50"
              value={amountRands}
              onChange={(e) => setAmountRands(e.target.value)}
            />
          </div>
        </div>

        <div className="vt-form-row">
          <div className="vt-field">
            <label>Your name</label>
            <input
              type="text"
              value={purchaserName}
              onChange={(e) => setPurchaserName(e.target.value)}
              required
            />
          </div>
          <div className="vt-field">
            <label>Email</label>
            <input
              type="email"
              value={purchaserEmail}
              onChange={(e) => setPurchaserEmail(e.target.value)}
              required
            />
          </div>
        </div>

        <div className="vt-field">
          <label>Phone (optional)</label>
          <input
            type="tel"
            value={purchaserPhone}
            onChange={(e) => setPurchaserPhone(e.target.value)}
          />
        </div>

        <label className="vt-checkbox-row">
          <input
            type="checkbox"
            checked={isForSomeoneElse}
            onChange={(e) => setIsForSomeoneElse(e.target.checked)}
          />
          <span>This is a gift for someone else</span>
        </label>

        {isForSomeoneElse && (
          <>
            <div className="vt-form-row">
              <div className="vt-field">
                <label>Recipient name</label>
                <input
                  type="text"
                  value={recipientName}
                  onChange={(e) => setRecipientName(e.target.value)}
                />
              </div>
              <div className="vt-field">
                <label>Recipient email</label>
                <input
                  type="email"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                />
              </div>
            </div>
            <div className="vt-field">
              <label>Recipient phone</label>
              <input
                type="tel"
                value={recipientPhone}
                onChange={(e) => setRecipientPhone(e.target.value)}
              />
            </div>
            <div className="vt-field">
              <label>Personal message</label>
              <textarea
                value={giftMessage}
                onChange={(e) => setGiftMessage(e.target.value)}
                rows={3}
                placeholder="Happy birthday! Enjoy an evening at Laborie."
              />
            </div>
          </>
        )}

        {error && <div className="vt-capacity-error">{error}</div>}

        <button type="submit" className="vt-cta" disabled={pending}>
          {pending
            ? "Redirecting to Paystack…"
            : `Pay ${currency} ${(parseFloat(amountRands || "0")).toLocaleString()} with Paystack →`}
        </button>

        <p className="vt-muted vt-text-center" style={{ fontSize: 11 }}>
          The card is issued the moment your payment succeeds
          {isForSomeoneElse ? " and emailed to the recipient." : "."}
        </p>
      </form>
      </div>
      </div>
      </div>
    </section>
  );
}
